import { StatusStep } from "../types/status-step";

/**
 * "12s", "1m 05s", "1h 02m": how long a turn worked, for the Worked card's header.
 * Null for nothing worth saying -- no measurement, or under half a second -- so the
 * header drops the figure rather than reading "0s".
 */
export const formatDuration = (ms: number | undefined): string | null => {
  if (ms === undefined || !Number.isFinite(ms) || ms < 500) return null;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
};

/**
 * What a finished turn did, in a few words beside "Worked": "3 steps · 12s", or
 * "3 steps, 1 failed · 12s" so a turn that half-worked does not read as one that worked.
 */
export const settledSummary = (steps: StatusStep[], durationMs?: number): string => {
  const failed = steps.filter((step) => step.status === "failed").length;
  const count = `${steps.length} ${steps.length === 1 ? "step" : "steps"}`;
  const outcome = failed > 0 ? `${count}, ${failed} failed` : count;
  return [outcome, formatDuration(durationMs)].filter(Boolean).join(" · ");
};

/**
 * What a turn in progress is doing, beside "Working": the step still running, or the
 * last one when none is -- the moment after a tool fails, while the model reads the
 * error -- and "Thinking…" before there are any.
 */
export const liveSummary = (steps: StatusStep[]): string => {
  const running = steps.find((step) => step.status === "active");
  return (running ?? steps[steps.length - 1])?.label ?? "Thinking…";
};
